import React, { useEffect, useState } from 'react';
import { Compass, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Sparkles, Loader2, RefreshCw, Lock } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

function severityStyle(sev) {
  switch (sev) {
    case 'good':
      return {
        pill:   'bg-emerald-50 text-emerald-700 border-emerald-200',
        border: 'border-l-emerald-400',
        Icon:   CheckCircle2,
        iconClass: 'text-emerald-500',
      };
    case 'warn':
      return {
        pill:   'bg-amber-50 text-amber-700 border-amber-200',
        border: 'border-l-amber-400',
        Icon:   AlertTriangle,
        iconClass: 'text-amber-500',
      };
    default:
      return {
        pill:   'bg-gray-100 text-gray-700 border-gray-200',
        border: 'border-l-gray-300',
        Icon:   Sparkles,
        iconClass: 'text-gray-400',
      };
  }
}

function fmtPct(n) {
  const num = Number(n || 0);
  return `${num.toFixed(1)}%`;
}

function fmtMoney(n) {
  const num = Number(n || 0);
  return `$${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch { return ''; }
}

const BusinessCoach = () => {
  const [data, setData]         = useState(null);   // { insights, metrics, generatedAt, note }
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const fetchInsights = async ({ manual = false } = {}) => {
    if (manual) setRefreshing(true); else setLoading(true);
    setError('');
    try {
      const token = await getAuth().currentUser?.getIdToken(false);
      if (!token) throw new Error('not_authenticated');
      const res = await fetch(`${API}/api/coach/insights`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await res.json().catch(() => ({}));
      if (res.status === 403) {
        setError('Business Coach is available to owners and admins only.');
        setData(null);
        return;
      }
      if (!res.ok) {
        throw new Error(payload.detail || 'Failed to generate insights');
      }
      setData(payload);
      if (manual) toast.success('Insights refreshed');
    } catch (err) {
      console.error('[BusinessCoach] fetchInsights error:', err);
      setError('We could not generate insights right now. Please try again in a moment.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchInsights(); }, []);

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="business-coach-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-[rgba(242,107,29,0.1)] flex items-center justify-center shrink-0">
            <Compass className="w-5 h-5 text-[var(--cb-orange)]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-secondary">Business Coach</h1>
            <p className="text-sm text-gray-500">
              Weekly insights from your real KRO data.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => fetchInsights({ manual: true })}
          disabled={loading || refreshing}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50"
          data-testid="coach-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Generated-at + fallback note */}
      {data?.generatedAt && (
        <div className="flex items-center gap-2 text-xs text-gray-400" data-testid="coach-generated-at">
          <Sparkles className="w-3.5 h-3.5" />
          <span>Generated {fmtTime(data.generatedAt)}</span>
          {data.note && (
            <span className="ml-2 text-amber-600" data-testid="coach-note">· {data.note}</span>
          )}
        </div>
      )}

      {/* Error / Access-denied state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="coach-error">
          <Lock className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !data && (
        <div className="space-y-3" data-testid="coach-loading">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
              <div className="h-4 w-1/3 bg-gray-200 rounded mb-3" />
              <div className="h-3 w-full bg-gray-100 rounded mb-1.5" />
              <div className="h-3 w-5/6 bg-gray-100 rounded" />
            </div>
          ))}
        </div>
      )}

      {/* Insights list */}
      {!loading && data?.insights?.length > 0 && (
        <div className="space-y-3" data-testid="coach-insights-list">
          {data.insights.map((ins, i) => {
            const s = severityStyle(ins.severity);
            return (
              <div
                key={i}
                className={`bg-white rounded-xl border border-gray-200 border-l-4 ${s.border} p-5 shadow-sm`}
                data-testid={`coach-insight-${i}`}
              >
                <div className="flex items-start gap-3">
                  <s.Icon className={`w-5 h-5 mt-0.5 shrink-0 ${s.iconClass}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <h3 className="text-sm font-semibold text-secondary">{ins.title}</h3>
                      {ins.severity && (
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${s.pill}`}>
                          {ins.severity}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{ins.body}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state — no data at all */}
      {!loading && !error && data && (!data.insights || data.insights.length === 0) && (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center" data-testid="coach-empty">
          <p className="text-sm text-gray-600">No insights yet — keep working and check back soon.</p>
        </div>
      )}

      {/* Snapshot strip — show the raw numbers the insights were built from */}
      {!loading && data?.metrics && (
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="coach-metrics-snapshot">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Snapshot</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <MetricCell
              label="Lead → Booked"
              value={fmtPct(data.metrics.leadToBookedRatePct)}
              sub={`${data.metrics.leadsBookedOrLater} of ${data.metrics.leadsTotal} leads`}
            />
            <MetricCell
              label="Quote close rate"
              value={fmtPct(data.metrics.quoteToCloseRatePct)}
              sub={`${data.metrics.quotesApproved} of ${data.metrics.quotesSentOrLater} quotes`}
            />
            <MetricCell
              label="Idle leads (14d)"
              value={String(data.metrics.idleLeadCount)}
              sub="No activity in 2 weeks"
            />
            <MetricCell
              label="Active plans"
              value={String(data.metrics.activeRecurringPlans)}
              sub="Recurring revenue base"
            />
            <MetricCell
              label="Avg quote · 30d"
              value={fmtMoney(data.metrics.avgQuoteSizeLast30)}
              sub={`Prev 30d: ${fmtMoney(data.metrics.avgQuoteSizePrev30)}`}
            />
            <MetricCell
              label="Quote size trend"
              value={`${data.metrics.quoteSizeDeltaPct >= 0 ? '+' : ''}${Number(data.metrics.quoteSizeDeltaPct).toFixed(1)}%`}
              sub="vs prior 30 days"
              Icon={data.metrics.quoteSizeDeltaPct >= 0 ? TrendingUp : TrendingDown}
              iconClass={data.metrics.quoteSizeDeltaPct >= 0 ? 'text-emerald-500' : 'text-amber-500'}
            />
          </div>
        </div>
      )}

      {/* Privacy footer */}
      <div className="flex items-center justify-center gap-2 text-xs text-gray-400 pt-2">
        <Lock className="w-3.5 h-3.5" />
        <span>Your data stays private to your company — never used to train external models.</span>
      </div>
    </div>
  );
};

const MetricCell = ({ label, value, sub, Icon, iconClass }) => (
  <div>
    <div className="text-xs text-gray-500 mb-0.5">{label}</div>
    <div className="flex items-center gap-1.5">
      {Icon && <Icon className={`w-4 h-4 ${iconClass || 'text-gray-400'}`} />}
      <span className="text-lg font-semibold text-secondary">{value}</span>
    </div>
    {sub && <div className="text-[11px] text-gray-400 mt-0.5">{sub}</div>}
  </div>
);

export default BusinessCoach;
